<template>
  <div class="intro-screen">
    <div class="content">
      <RichTextRenderer :document="content.description" />
    </div>

    <div class="intro-block swiper-container">
      <swiper
        ref="swiper"
        :grab-cursor="true"
        :spaceBetween="30"
        :effect="'fade'"
        :pagination="{
          clickable: true,
        }"
        :loop="true"
        :modules="modules"
      >
        <swiper-slide
          v-for="item in content.images"
          class="swiper-card"
          :key="item.fields.file.fileName"
        >
          <img
            :src="item.fields.file.url"
            :alt="item.fields.file.title"
            class="swiper-card-image"
          />

          <div
            v-if="setPhotographer(item.fields.description)?.length"
            class="credits photographer"
            @click="goToLink(setPhotographer(item.fields.description))"
          >
            📷{{ setPhotographer(item.fields.description) }}
          </div>

          <div
            v-if="setCosplayer(item.fields.description)?.length"
            class="credits cosplayer"
            @click="goToLink(setCosplayer(item.fields.description))"
          >
            🙂{{ setCosplayer(item.fields.description) }}
          </div>
        </swiper-slide>

        <div class="swiper-card-border"></div>
      </swiper>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from "swiper/vue";
import appear from "../helper/appear";
import RichTextRenderer from "contentful-rich-text-vue-renderer";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import { EffectFade, Navigation, Pagination } from "swiper";

export default {
  name: "intro-screen",
  props: {
    content: [Array, Object],
  },
  components: {
    RichTextRenderer,
    Swiper,
    SwiperSlide,
  },
  mixins: [appear],
  setup() {
    return {
      modules: [EffectFade, Navigation, Pagination],
    };
  },
  methods: {
    setPhotographer(dataset) {
      const photographerMatch = dataset?.match(/photographer:\s*([^\n]+)/i);

      return photographerMatch ? photographerMatch[1].trim() : "";
    },
    setCosplayer(dataset) {
      const cosplayerMatch = dataset?.match(/cosplayer:\s*([^\n]+)/i);

      return cosplayerMatch ? cosplayerMatch[1].trim() : "";
    },
    goToLink(name) {
      if (name) {
        window.open(`https://www.instagram.com/${name}`, "_blank");
      }
    },
  },
};
</script>

<style lang="scss">
.intro-screen {
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  gap: $spacing-md;
  height: 100%;
  transition: all 1.2s ease;
  margin-bottom: $spacing-md;

  @media (min-width: $lg) {
    flex-direction: row;
    margin-bottom: calc(#{$spacing-lg} * 2);
  }

  .content {
    flex: 1;

    * {
      color: $sewcase-black;

      .contest-page.dark-mode & {
        color: $sewcase-white;
      }
    }

    a {
      text-decoration: underline;
    }
  }

  .intro-block {
    display: flex;
    justify-content: flex-end;
  }
}

.swiper-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  .swiper {
    width: 275px;
    overflow: visible;

    @media (min-width: $sm) {
      width: 375px;
      margin: 0;
    }
  }

  .swiper-wrapper:before {
    content: "";
    position: absolute;
    background: $sewcase-black;
    border-radius: $border-radius-sm;
    right: -20px;
    bottom: -20px;
    width: 100%;
    height: 100%;
    transition: all 0.2s ease;
    z-index: -1;
  }

  .swiper-slide {
    border: 3px solid $sewcase-black;
    border-radius: $border-radius-sm;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: bold;
    overflow: hidden;

    img {
      aspect-ratio: 1;
      object-fit: cover;
      width: 100%;
      user-select: none;
    }

    .credits {
      cursor: pointer;
      position: absolute;
      left: 4px;
      bottom: 4px;
      background: #fafafacc;
      border-radius: 4px;
      padding: 2px;
      font-size: 12px;
      transition: 0.3s ease;

      &:hover {
        background: #fafafa;
      }
    }

    .cosplayer {
      left: unset;
      right: 4px;
    }
  }

  .swiper-slide-visible {
    .swiper-card-cosplayer {
      opacity: 1;
    }
  }

  .swiper-card-image,
  .swiper-card-headline,
  .swiper-card-growth {
    opacity: 0.1;
    transition: 0.3s ease;
  }

  .swiper-slide-duplicate::before {
    content: "";
    cursor: default;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    user-select: none;
    pointer-events: none;
  }

  .swiper-slide-active .swiper-card-image,
  .swiper-slide-active .swiper-card-headline,
  .swiper-slide-active .swiper-card-growth {
    opacity: 1;
    transform: scale(1.025);
  }

  .swiper:before,
  .swiper:after {
    background: none;
  }

  .swiper-button-prev,
  .swiper-button-next {
    &:after {
      content: "";
      background: transparent;
      border-bottom: 16px solid transparent;
      border-top: 16px solid transparent;
      height: 32px;
      width: 0.3%;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      transition: 0.3s ease;
    }
  }

  .swiper-button-prev {
    left: -32px;

    @media (min-width: $md) {
      left: -48px;
    }

    &:after {
      border-right: 24px solid $sewcase-grey;
    }

    &:hover {
      &:after {
        border-right: 24px solid $sewcase-secondary;
      }
    }
  }

  .swiper-button-next {
    right: -32px;

    @media (min-width: $md) {
      right: -48px;
    }

    &:after {
      border-left: 24px solid $sewcase-grey;
    }

    &:hover {
      &:after {
        border-left: 24px solid $sewcase-secondary;
      }
    }
  }

  .swiper-pagination {
    border-radius: $border-radius-sm;
    background: $sewcase-secondary;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: -24px;
    width: 90%;
    padding: 4px;

    .contest-page & {
      background: $sewcase-nb-gold;
    }

    @media (min-width: $md) {
      bottom: -36px;
      width: 80%;
    }

    .swiper-pagination-bullet {
      background: $sewcase-black;
      opacity: 0.9;
    }

    .swiper-pagination-bullet-active {
      background: $sewcase-white;
    }
  }
}
</style>
